import type { RootState } from 'src/redux/store';

import { useMemo } from 'react';

import { useAppSelector } from 'src/hook/hooks';

import { PremissionContext } from './permissions-context';
import { usePermissionChecks } from './usePermissionChecks';
import { usePermissionsSource } from './usePermissionsSource';
import { useNavPermissionFilter } from './useNavPermissionFilter';
import { useActiveSubscriptionEffect } from './useActiveSubscriptionEffect';

type PermissionProviderProps = {
  children: React.ReactNode;
};

export function PremissionProvider({ children }: PermissionProviderProps) {
  const loginApiData = useAppSelector((state: RootState) => state.auth.loginApiData);

  const permissions = usePermissionsSource(loginApiData);
  useActiveSubscriptionEffect(loginApiData, permissions);
  const userRole = loginApiData && 'role' in loginApiData ? (loginApiData.role as string) : null;
  const { hasPermission, hasModuleActionPermission } = usePermissionChecks(permissions, userRole);
  const handleNavPermissionCheck = useNavPermissionFilter({ permissions, loginApiData });

  const contextValue = useMemo(
    () => ({
      handleNavPremissionCheck: handleNavPermissionCheck,
      hasPermission,
      hasModuleActionPermission,
      permissions,
    }),
    [handleNavPermissionCheck, hasModuleActionPermission, hasPermission, permissions]
  );

  return <PremissionContext.Provider value={contextValue}>{children}</PremissionContext.Provider>;
}
